import Pagination from 'elements/pagination/Pagination'
import { Notification, Skeleton, styled, Typography } from 'opensolar-ui'
import { LenderType } from 'opensolar-ui/src/checkout/checkoutFinancing/types'
import { BaseThemeType, useMediaQuery } from 'opensolar-ui/src/theme'
import { useEffect, useMemo, useState } from 'react'
import restClient from 'restClient'
import {
  CheckoutFinancingFilterParameters,
  CheckoutFinancingPricingOption,
  CheckoutFinancingPricingParams,
} from '../types'
import FinanceMarketplaceApplicationStatus from './FinanceMarketplaceApplicationStatus'
import FinanceMarketplaceSorting from './FinanceMarketplaceSorting'
import FinanceMarketplaceSummary from './FinanceMarketplaceSummary'
import MarketplacePricingOptionRows from './MarketplacePricingOptionRows'

export type ExitSurveyConfig = {
  should_see_dialog: boolean
  question: string
  answers: string[]
}

const restClientInstance = restClient(window.API_ROOT + '/api')

type PropTypes = {
  pricingOptions: CheckoutFinancingPricingOption[]
  lenders: LenderType[]
  isLoading: boolean
  translate: (text: string) => string
  updatePricingParams: (newParams: CheckoutFinancingPricingParams) => void
  pricingParams: CheckoutFinancingPricingParams
  filterParameters: CheckoutFinancingFilterParameters
  totalPricingOptions: number
  setHideDialog: (hide: boolean) => void
  closeRootDialog: () => void
  onExitSurveyConfigLoaded?: (config: ExitSurveyConfig) => void
  onApplicationStarted?: () => void
  fromOptimizedCheckout?: boolean
}

const MainWrapper = styled('div')<{ isMobile: boolean }>(({ isMobile }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  minHeight: '400px',
  minWidth: isMobile ? '200px' : '400px',
}))

const DisclaimerWrapper = styled('div')(({ theme }: { theme: BaseThemeType }) => ({
  marginBottom: '16px',
  padding: '16px',
  borderRadius: '10px',
  backgroundColor: theme.palette.grey[100],
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}))

const DisclaimerText = styled(Typography)(({ theme }: { theme: BaseThemeType }) => ({
  fontSize: '14px',
}))

const NotificationWrapper = styled('div')({
  '& .OSUI-Alert-root': {
    width: 'auto',
  },
})

const FinanceMarketplaceDialogContent = (props: PropTypes) => {
  const {
    pricingOptions,
    translate,
    isLoading,
    pricingParams,
    updatePricingParams,
    filterParameters,
    totalPricingOptions,
    setHideDialog,
    closeRootDialog,
    onExitSurveyConfigLoaded,
    onApplicationStarted,
    fromOptimizedCheckout = false,
  } = props

  const [isAwaitingRefresh, setIsAwaitingRefresh] = useState(false)

  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('sm'))

  useEffect(() => {
    if (!pricingParams?.org_id || !pricingParams?.project_id || !onExitSurveyConfigLoaded) return
    restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${pricingParams.org_id}/projects/${pricingParams.project_id}/checkout_financing/exit_survey_config/`,
    })
      .then((response: { data: ExitSurveyConfig }) => {
        onExitSurveyConfigLoaded(response.data)
      })
      .catch(() => {
        onExitSurveyConfigLoaded({ should_see_dialog: false, question: '', answers: [] })
      })
  }, [pricingParams?.org_id, pricingParams?.project_id, onExitSurveyConfigLoaded])

  const onPageChange = (newPage: number) => {
    setIsAwaitingRefresh(true)
    updatePricingParams({ ...pricingParams, page: newPage })
  }

  const onPerPageChange = (newPerPage: number) => {
    setIsAwaitingRefresh(true)
    updatePricingParams({ ...pricingParams, limit: newPerPage })
  }

  // this is only used to prevent duplicate amplitud events from being logged while we wait
  // for debounced pagination changes to result in new rows to render
  useEffect(() => {
    setIsAwaitingRefresh(false)
  }, [props.pricingOptions])

  const showPropensioDisclaimer = useMemo(() => {
    return pricingOptions.some((pricingOption) => pricingOption.lender_integration_name === 'propensio')
  }, [pricingOptions])

  return (
    <MainWrapper isMobile={isMobile}>
      <FinanceMarketplaceApplicationStatus projectId={pricingParams.project_id} orgId={pricingParams.org_id} />
      <FinanceMarketplaceSummary
        pricingParams={pricingParams}
        updatePricingParams={updatePricingParams}
        filterParameters={filterParameters}
        isLoading={isLoading}
      />
      <FinanceMarketplaceSorting updatePricingParams={updatePricingParams} pricingParams={pricingParams} />
      {isLoading && (
        <div>
          {[...Array(4)].map((_, idx) => (
            <Skeleton key={idx} height={200} width={900} />
          ))}
        </div>
      )}
      {pricingOptions?.length > 0 && !isLoading && (
        <>
          {pricingOptions.map((pricingOption, i) => (
            <MarketplacePricingOptionRows
              key={pricingOption.total_repayable}
              pricingOption={pricingOption}
              pricingParams={pricingParams}
              setHideDialog={setHideDialog}
              closeRootDialog={closeRootDialog}
              onApplicationStarted={onApplicationStarted}
              fromOptimizedCheckout={fromOptimizedCheckout}
              rank={i + 1 + (pricingParams.page - 1) * pricingParams.limit}
              isAwaitingRefresh={isAwaitingRefresh}
            />
          ))}
          <Pagination
            setPage={onPageChange}
            setPerPage={onPerPageChange}
            page={pricingParams.page}
            perPage={pricingParams.limit}
            rowsPerPageOptions={[5, 10]}
            total={totalPricingOptions}
          />
        </>
      )}
      {!pricingOptions?.length && !isLoading && (
        <NotificationWrapper>
          <Notification
            severity="warning"
            title={translate('No products match the budget')}
            message={translate('Try increasing your term length or maximum monthly repayment.')}
          />
        </NotificationWrapper>
      )}
      <DisclaimerWrapper>
        <DisclaimerText textVariant="body2">
          Finance Explorer is provided by OpenSolar for illustrative purposes only and does not constitute an offer of
          credit. All finance options are subject to your application, your individual financial circumstances, and
          lender's approval. Interest rates and terms may vary based on your credit score and the specific product
          chosen.
        </DisclaimerText>
        <DisclaimerText textVariant="body2">
          OpenSolar does not provide or arrange credit. OpenSolar may introduce you to a limited number of finance
          providers. If you decide to apply, your application will be made directly with the lender. OpenSolar may
          receive a fixed commission from the lender, which does not affect the rate or outcome of your application.
        </DisclaimerText>
        {showPropensioDisclaimer && (
          <DisclaimerText textVariant="body2">
            OpenSolar Pty Ltd is an Introducer Appointed Representative of Propensio Finance Ltd. and Selina Finance
            Ltd., which are authorised and regulated by the Financial Conduct Authority for secured lending. We do not
            provide financial advice. Please seek independent financial advice if you are unsure. OpenSolar may receive
            a fixed commission, which does not affect the amount you pay
          </DisclaimerText>
        )}
      </DisclaimerWrapper>
    </MainWrapper>
  )
}
export default FinanceMarketplaceDialogContent
